import { useState, useRef, useEffect } from 'react';
import { jobs, type Job } from './jobsData';
import { fireSwipeEvent } from '../../../../utils/tracking';

const PRIMARY = '#fa7c9e';
const PRIMARY_DARK = '#ff5c73';

const cardStyle: React.CSSProperties = {
  flexShrink: 0,
  width: '85vw',
  maxWidth: '380px',
  background: '#fff',
  border: '1px solid #f1d6de',
  borderRadius: '12px',
  padding: '16px',
  boxShadow: '0 2px 6px rgba(250, 124, 158, 0.08)',
  scrollSnapAlign: 'start',
};

const tagStyle: React.CSSProperties = {
  display: 'inline-block',
  background: PRIMARY,
  color: '#fff',
  fontSize: '12px',
  fontWeight: 700,
  padding: '4px 12px',
  borderRadius: '999px',
  marginBottom: '12px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '11px',
  color: '#888',
  marginBottom: '2px',
};

const monthlyStyle: React.CSSProperties = {
  fontSize: '18px',
  fontWeight: 700,
  color: '#222',
  fontFamily: "'Zen Maru Gothic', sans-serif",
};

const annualStyle: React.CSSProperties = {
  fontSize: '16px',
  fontWeight: 700,
  color: PRIMARY_DARK,
  fontFamily: "'Zen Maru Gothic', sans-serif",
};

const detailRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  fontSize: '12px',
  color: '#444',
  lineHeight: 1.5,
};

const iconWrapStyle: React.CSSProperties = {
  width: '16px',
  height: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  marginTop: '2px',
  color: PRIMARY,
};

interface DetailRowProps {
  icon: string;
  label: string;
  value: string;
}

function DetailRow({ icon, label, value }: DetailRowProps) {
  return (
    <div style={detailRowStyle}>
      <div style={iconWrapStyle}>
        <i className={`${icon} ri-fw`} style={{ fontSize: '14px' }} />
      </div>
      <div>
        <span style={{ fontWeight: 700 }}>{label}:</span> {value}
      </div>
    </div>
  );
}

function JobCard({ job, index }: { job: Job; index: number }) {
  return (
    <div
      style={cardStyle}
      data-track-view="view_card"
      data-element-type="job_card"
      data-element-name={`job_${job.id}_${job.hospitalType}`}
      data-element-index={index}
    >
      <div style={tagStyle}>{job.hospitalType}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div>
          <div style={labelStyle}>月収</div>
          <div style={monthlyStyle}>{job.monthlySalary}</div>
        </div>

        <div>
          <div style={labelStyle}>年収</div>
          <div style={annualStyle}>{job.annualSalary}</div>
        </div>

        <div
          style={{
            borderTop: '1px solid #f3e5ea',
            paddingTop: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          <DetailRow icon="ri-time-line" label="勤務形態" value={job.workStyle} />
          {job.overtime && (
            <DetailRow icon="ri-timer-line" label="残業" value={job.overtime} />
          )}
          <DetailRow icon="ri-hospital-line" label="診療科" value={job.department} />
          {job.employmentType && (
            <DetailRow icon="ri-briefcase-line" label="雇用形態" value={job.employmentType} />
          )}
          {job.qualification && (
            <DetailRow icon="ri-award-line" label="資格" value={job.qualification} />
          )}
          <DetailRow
            icon="ri-money-dollar-circle-line"
            label="手当"
            value={job.allowance}
          />
          <DetailRow icon="ri-gift-line" label="賞与" value={job.bonus} />
          {job.benefits && (
            <DetailRow icon="ri-heart-line" label="福利厚生" value={job.benefits} />
          )}
          <DetailRow icon="ri-calendar-line" label="休日" value={job.holidays} />
          <DetailRow icon="ri-map-pin-line" label="勤務地" value={job.location} />
        </div>
      </div>
    </div>
  );
}

export default function JobCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastSwipeIndexRef = useRef<number>(0);
  const swipeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      // カード幅 + gap で割って近いカードを取る
      const firstCard = container.querySelector<HTMLDivElement>('[data-element-type="job_card"]');
      if (!firstCard) return;
      const cardWidth = firstCard.offsetWidth + 12; // gap = 12px
      const newIndex = Math.round(scrollLeft / cardWidth);
      const clampedIndex = Math.min(Math.max(newIndex, 0), jobs.length - 1);
      setCurrentIndex(clampedIndex);

      // スワイプイベントを発火（スクロール停止後300ms、かつインデックスが変わった時のみ）
      if (swipeTimerRef.current) clearTimeout(swipeTimerRef.current);
      swipeTimerRef.current = setTimeout(() => {
        if (clampedIndex !== lastSwipeIndexRef.current) {
          const direction: 'left' | 'right' =
            clampedIndex > lastSwipeIndexRef.current ? 'left' : 'right';
          const job = jobs[clampedIndex];
          fireSwipeEvent({
            card_id: job.id,
            card_index: clampedIndex,
            card_name: job.hospitalType,
            carousel_name: 'job_cards',
            direction,
          });
          lastSwipeIndexRef.current = clampedIndex;
        }
      }, 300);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (swipeTimerRef.current) clearTimeout(swipeTimerRef.current);
    };
  }, []);

  return (
    <div style={{ position: 'relative' }} data-element-type="carousel">
      <div
        ref={scrollContainerRef}
        className="job-carousel-scroll"
        style={{
          display: 'flex',
          gap: '12px',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          paddingBottom: '8px',
          // hide scrollbar (Firefox)
          scrollbarWidth: 'none',
          // hide scrollbar (IE/Edge legacy)
          msOverflowStyle: 'none',
        }}
      >
        {jobs.map((job, index) => (
          <JobCard key={job.id} job={job} index={index} />
        ))}
      </div>

      {/* インジケーター */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          marginTop: '14px',
        }}
      >
        {jobs.map((_, index) => (
          <div
            key={index}
            style={{
              height: '6px',
              borderRadius: '999px',
              transition: 'all 0.25s ease',
              width: index === currentIndex ? '22px' : '6px',
              background: index === currentIndex ? PRIMARY : '#e8d3da',
            }}
          />
        ))}
      </div>
    </div>
  );
}
